import i18next from 'i18next';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import arrowWhiteIcon from '../../../assets/img/icons/arrow_right_white.svg';
import ComponentCircleLoader from '../../../components/Loaders/ComponentCircleLoader';
import LinearActivityBar from '../../../components/ProgressBar/LinearActivityBar';
import NoDataComponent from '../../../components/NoDataComponent/NoDataComponent';
import {
  TableItem,
  TableRowAndBudgetCollapseContainer,
} from '../../../styledComponents/ProjectsNew';
import {
  CommonFlex,
  Container,
  TableText,
} from '../../../styledComponents/common';
import { CheckListStatus, TaskTable } from '../../../styledComponents/tasks';
import { ExpandIcon, ProjectExpandButton } from '../../Projects/projectsStyles';
import { CollapseSection } from '../tasksStyles';
import Status from 'components/Status/Status';

// Grid for main task row: Task Name, Task code, Office, Location Name, Status, Expand
const mainGridTemplate = 'minmax(200px, 2fr) minmax(120px, 0.5fr) minmax(120px, 0.5fr) 120px minmax(100px, 0.5fr) 44px';

// Subtask grid: narrower task code column to avoid UUID overflow
const subtaskGridTemplate = 'minmax(180px, 2fr) minmax(120px, 0.5fr) minmax(100px, 0.5fr) minmax(100px, 0.5fr) minmax(110px, 0.5fr) minmax(100px, 0.5fr)';

const SubtasksPanel = styled.div`
  margin: 0 16px 16px;
  padding: 16px 20px;
  background: #f5f6f9;
  border-radius: 8px;
  border: 1px solid #e8eaef;
`;

const SubtasksTitle = styled.span`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #434343;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8eaef;
`;

const SubtaskRow = styled(TaskTable)`
  padding: 10px 0;
  align-items: center;
  min-height: 44px;
  border-bottom: none;
  :nth-of-type(even) {
    background-color: rgba(255, 255, 255, 0.6);
    border-radius: 4px;
  }
`;

const TruncateCell = styled.span`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: inherit;
  cursor: ${({ $hasTitle }) => ($hasTitle ? 'help' : 'default')};
`;

const truncateCode = (code, maxLen = 12) => {
  if (!code || code === '-') return '-';
  const str = String(code);
  if (str.length <= maxLen) return str;
  return `${str.slice(0, 8)}…`;
};

const EmtithalOfficeTasksList = ({
  taskList,
  isLoading = false,
  expandedTaskId,
  setExpandedTaskId,
}) => {
  const { t } = useTranslation();

  const handleExpand = taskId => {
    if (expandedTaskId === taskId) {
      setExpandedTaskId(null);
    } else {
      setExpandedTaskId(taskId);
    }
  };

  const getTaskCode = task => task.task_code ?? task.code ?? '-';
  const getTaskName = task => task.name || '-';
  const getOfficeName = task =>
    task.office_name ?? (task.office && (task.office.name || task.office)) ?? '-';
  const getLocationName = task =>
    task.public_zone_name ??
    (task.location && (task.location.name || task.location)) ??
    '-';
  const getStatusPercent = task => {
    const subTasks = task.sub_tasks_status || task.sub_tasks || [];
    if (!subTasks.length) return 0;
    let total = 0;
    subTasks.forEach(st => {
      const status = st.status || '';
      const match = String(status).match(/(\d+)/);
      total += match ? parseInt(match[1], 10) : 0;
    });
    return Math.round(total / subTasks.length);
  };

  const getSubTasks = task =>
    task.sub_tasks_status || task.sub_tasks || task.subtasks || task.children || [];

  const getSubTaskCode = st =>
    st.inspection_sub_task_id ?? st.task_code ?? st.code ?? st.sub_task_code ?? '-';
  const getSubTaskName = st =>
    st.inspection_sub_task_name ?? st.default_sub_task_name ?? st.name ?? '-';
  const getSubTaskOffice = (st, task) =>
    task?.office_name ?? (st.office && (st.office.name || st.office)) ?? '-';
  const getSubTaskLocation = (st, task) =>
    task?.public_zone_name ??
    (st.location && (st.location.name || st.location)) ??
    '-';
  const getSubTaskLastStatus = st =>
    st.status ?? st.last_status ?? st.completion_percentage ?? '-';
  const getSubTaskDateTime = st => {
    const d = st.last_updated ?? st.updated_at ?? st.date_time ?? st.completed_at;
    return d ? moment(d).format('YYYY/M/D HH:mm') : '-';
  };

  if (isLoading) {
    return (
      <Container style={{ paddingBottom: '20px' }}>
        <ComponentCircleLoader />
      </Container>
    );
  }

  if (!taskList || taskList.length === 0) {
    return (
      <Container style={{ paddingBottom: '20px' }}>
        <NoDataComponent
          title={t('no_emtithal_office_tasks_found')}
          padding="5vh 0"
          imageHeight="140px"
        />
      </Container>
    );
  }


  return (
    <Container style={{ paddingBottom: '20px' }}>
      <TaskTable
        heading
        style={{
          gridTemplateColumns: mainGridTemplate,
          padding: '12px 16px',
          alignItems: 'center',
        }}
      >
        <TableItem>
          <TableText>{t('task_name')}</TableText>
        </TableItem>
        <TableItem>
          <TableText>{t('task_code')}</TableText>
        </TableItem>
        <TableItem>
          <TableText>{t('office')}</TableText>
        </TableItem>
        <TableItem>
          <TableText>{t('location_name')}</TableText>
        </TableItem>
        <TableItem>
          <TableText>{t('status')}</TableText>
        </TableItem>
        <TableItem />
      </TaskTable>
      {taskList.map(task => {
        const subTasks = getSubTasks(task);
        const percent = getStatusPercent(task);
        const isExpanded = expandedTaskId === task.id;
        return (
          <TableRowAndBudgetCollapseContainer key={task.id}>
            <TaskTable
              style={{
                gridTemplateColumns: mainGridTemplate,
                padding: '12px 16px',
                alignItems: 'center',
              }}
            >
              <TableItem>
                <TruncateCell
                  title={getTaskName(task)}
                  $hasTitle={!!getTaskName(task)}
                >
                  {getTaskName(task)}
                </TruncateCell>
              </TableItem>
              <TableItem>
                <TruncateCell
                  title={getTaskCode(task)}
                  $hasTitle={!!getTaskCode(task)}
                >
                  {truncateCode(getTaskCode(task), 14)}
                </TruncateCell>
              </TableItem>
              <TableItem>
                <TruncateCell
                  title={getOfficeName(task)}
                  $hasTitle={!!getOfficeName(task)}
                >
                  {getOfficeName(task)}
                </TruncateCell>
              </TableItem>
              <TableItem>
                <TruncateCell
                  title={getLocationName(task)}
                  $hasTitle={!!getLocationName(task)}
                >
                  {getLocationName(task)}
                </TruncateCell>
              </TableItem>
              <TableItem>
                <CommonFlex direction="column" gap="2px">
                  <TableText name>{percent}%</TableText>
                  <LinearActivityBar
                    width="50px"
                    mobileWidth="50px"
                    height="10px"
                    mobileHeight="8px"
                    borderRadius="5px"
                    red="#FE5151"
                    yellow="#F2C741"
                    green="#20bead"
                    percent={percent}
                    marginTop="0"
                    backgroundColor="#B5B5B5"
                  />
                </CommonFlex>
              </TableItem>
              <TableItem>
                <ProjectExpandButton
                  margin="0"
                  onClick={() => handleExpand(task.id)}
                >
                  <ExpandIcon
                    src={arrowWhiteIcon}
                    alt=""
                    open={isExpanded}
                  />
                </ProjectExpandButton>
              </TableItem>
            </TaskTable>
            <CollapseSection open={isExpanded}>
              {subTasks.length > 0 ? (
                <SubtasksPanel>
                  <SubtasksTitle>{t('subtasks')}</SubtasksTitle>
                  <TaskTable
                    heading
                    style={{
                      gridTemplateColumns: subtaskGridTemplate,
                      padding: '8px 0',
                      marginBottom: '4px',
                    }}
                  >
                    <TableItem>
                      <TableText>{t('sub_task_name')}</TableText>
                    </TableItem>
                    <TableItem>
                      <TableText>{t('task_code')}</TableText>
                    </TableItem>
                    <TableItem>
                      <TableText>{t('office')}</TableText>
                    </TableItem>
                    <TableItem>
                      <TableText>{t('location_name')}</TableText>
                    </TableItem>
                    <TableItem>
                      <TableText>{t('last_status')}</TableText>
                    </TableItem>
                    <TableItem>
                      <TableText>{t('date_time')}</TableText>
                    </TableItem>
                  </TaskTable>
                  {subTasks.map((st, idx) => (
                    <SubtaskRow
                      key={
                        st.inspection_sub_task_id ||
                        st.default_sub_task_id ||
                        st.id ||
                        `subtask-${task.id}-${idx}`
                      }
                      style={{ gridTemplateColumns: subtaskGridTemplate }}
                    >
                      <TableItem>
                        <TruncateCell
                          title={getSubTaskName(st)}
                          $hasTitle={!!getSubTaskName(st)}
                        >
                          {getSubTaskName(st)}
                        </TruncateCell>
                      </TableItem>
                      <TableItem>
                        <TruncateCell
                          title={getSubTaskCode(st)}
                          $hasTitle={!!getSubTaskCode(st)}
                        >
                          {truncateCode(getSubTaskCode(st))}
                        </TruncateCell>
                      </TableItem>
                      <TableItem>
                        <TruncateCell
                          title={getSubTaskOffice(st, task)}
                          $hasTitle={!!getSubTaskOffice(st, task)}
                        >
                          {getSubTaskOffice(st, task)}
                        </TruncateCell>
                      </TableItem>
                      <TableItem>
                        <TruncateCell
                          title={getSubTaskLocation(st, task)}
                          $hasTitle={!!getSubTaskLocation(st, task)}
                        >
                          {getSubTaskLocation(st, task)}
                        </TruncateCell>
                      </TableItem>
                      <TableItem>
                        {/* {typeof getSubTaskLastStatus(st) === 'number' ? (
                          <TableText name>{getSubTaskLastStatus(st)}%</TableText>
                        ) : (
                          <CheckListStatus status={getSubTaskLastStatus(st)}>
                            {i18next.language === 'en'
                              ? String(getSubTaskLastStatus(st)).toUpperCase()
                              : String(getSubTaskLastStatus(st))}
                          </CheckListStatus>
                        )} */}
                        <Status showDot={false} status={st.status === "to do" ? "0" : st.status} /> 
                      </TableItem>
                      <TableItem>
                        <TableText name>{getSubTaskDateTime(st)}</TableText>
                      </TableItem>
                    </SubtaskRow>
                  ))}
                </SubtasksPanel>
              ) : (
                <SubtasksPanel>
                  <TableText name>{t('no_subtasks')}</TableText>
                </SubtasksPanel>
              )}
            </CollapseSection>
          </TableRowAndBudgetCollapseContainer>
        );
      })}
    </Container>
  );
};

export default EmtithalOfficeTasksList;
